import { GeneralApiError } from '../domain/errors/GeneralApiError';
import { TokenDTO, getXsrfTokenHeader, setXsrfToken } from './xsrfTokenHandler';
import { getLogger } from '../utils/logger/logger';
import { apiErrorMapper } from '../utils/apiErrorMapper.util';
import { isNetworkError, NetworkError } from '@/domain/errors/NetworkError';

const parseJson = async (response: Response) => {
  try {
    return await response.json();
  } catch {
    getLogger('parseJson').error('Could not parse JSON.');
    throw new GeneralApiError();
  }
};

const fetchRequest = async <Response, Body = undefined>({
  method,
  url,
  body,
  tokenName,
}: {
  method: string;
  url: string;
  body?: Body;
  tokenName?: TokenDTO;
}): Promise<Response | null> => {
  const headers = getXsrfTokenHeader(tokenName);
  headers.set('Accept', 'application/json; charset=utf-8');
  if (body) headers.set('Content-Type', 'application/json; charset=utf-8');

  try {
    const response = await fetch(url, {
      method,
      headers,
      credentials: 'include',
      body: body ? JSON.stringify(body) : undefined,
    });
    setXsrfToken(response, tokenName);

    if (response.ok) {
      if (response.headers.get('Content-Type')?.startsWith('application/json') && response.status !== 204) {
        return await parseJson(response);
      }
      return null;
    }
    return Promise.reject(await apiErrorMapper(url, response));
  } catch (error) {
    if (isNetworkError(error)) {
      throw new NetworkError();
    }
    throw await apiErrorMapper(url);
  }
};

export const get = async <T>(url: string, tokenName?: TokenDTO): Promise<T> => {
  const response = await fetchRequest<T>({ method: 'GET', url, tokenName });
  if (response !== null) return response;
  throw new GeneralApiError();
};

export const post = <Response, Body>(url: string, body?: Body, tokenName?: TokenDTO): Promise<Response | null> =>
  fetchRequest<Response, Body>({ method: 'POST', url, body, tokenName });
export const put = <Response, Body>(url: string, body?: Body, tokenName?: TokenDTO): Promise<Response | null> =>
  fetchRequest<Response, Body>({ method: 'PUT', url, body, tokenName });
export const patch = <Response, Body>(url: string, body?: Body, tokenName?: TokenDTO): Promise<Response | null> =>
  fetchRequest<Response, Body>({ method: 'PATCH', url, body, tokenName });
export const del = <T>(url: string, tokenName?: TokenDTO): Promise<T | null> =>
  fetchRequest<T>({ method: 'DELETE', url, tokenName });
